import React from 'react';
import { motion } from 'framer-motion';
export interface MapScaleProps {
  /** The scale ratio, e.g. 10000 means 1:10.000 */
  scaleRatio: number;
  /** Length on the map in cm */
  mapLength?: number;
  /** Real-world length (calculated or provided) */
  realLength?: number;
  /** Unit for real-world length */
  unit?: 'cm' | 'm' | 'km';
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function formatNL(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(n);
}
function formatScale(ratio: number): string {
  return `1 : ${new Intl.NumberFormat('nl-NL').format(ratio)}`;
}
const UNIT_LABELS: Record<string, string> = {
  cm: 'centimeter',
  m: 'meter',
  km: 'kilometer'
};
export function MapScale({
  scaleRatio,
  mapLength,
  realLength,
  unit = 'km',
  label,
  mode = 'support'
}: MapScaleProps) {
  if (!scaleRatio || scaleRatio <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13648" data-mp="hbcen4akm9gt">
        <p className="text-red-600 text-sm font-medium" data-id="element-13649" data-mp="da0jkj8xgw18">Ongeldige schaal</p>
      </div>;
  }
  // Calculate real length if map length is provided
  const calculatedReal = mapLength !== undefined ? mapLength * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) : realLength;
  // Calculate map length if real length is provided
  const calculatedMap = realLength !== undefined && mapLength === undefined ? realLength * (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) / scaleRatio : mapLength;
  // Scale bar segments (visual representation)
  const segments = 4;
  const segmentWidths = Array.from({
    length: segments
  }, (_, i) => i + 1);
  // Stimulus mode: compact scale bar
  if (mode === 'stimulus') {
    return <div className="bg-emerald-50 rounded-xl px-5 py-4 border border-emerald-200" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-13650" data-mp="3bz7qplm4yd81">
        
        {label && <p className="text-sm font-semibold text-emerald-800 mb-3 text-center" data-id="element-13651" data-mp="1azmaranwzgke">
            {label}
          </p>}
        {/* Scale ratio */}
        <div className="text-center mb-3" data-id="element-13652" data-mp="1fvt7ccuf7fpo">
          <span className="inline-flex items-center bg-white rounded-lg px-3 py-1.5 border border-emerald-200 font-mono text-sm font-bold text-emerald-900" data-id="element-13653" data-mp="1ko49lmcmvdle">
            {formatScale(scaleRatio)}
          </span>
        </div>
        {/* Scale bar */}
        <div className="flex justify-between text-[9px] text-slate-500 mb-0.5" style={{
        width: `${segments * 48}px`,
        margin: '0 auto'
      }} data-id="element-13654" data-mp="s1tbcici1orr">
          
          {Array.from({
          length: segments + 1
        }, (_, i) => <span key={i} className="w-4 text-center -ml-2" data-id="element-13655" data-mp="15zhw7q6kgvwt">
                {i}
              </span>)}
        </div>
        <div className="flex items-end justify-center gap-0" data-id="element-13656" data-mp="2cglfu999uv4y">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: i * 0.1,
          duration: 0.3
        }} className={`h-3 w-12 border border-emerald-400 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-13657" data-mp="2hedgbrnzfpkr" data-mpi={i} />)}
        </div>
        <div className="flex justify-between text-[9px] text-emerald-700 font-bold mt-0.5" style={{
        width: `${segments * 48}px`,
        margin: '0 auto'
      }} data-id="element-13658" data-mp="2hr7dk3mso5bs">
          
          {Array.from({
          length: segments + 1
        }, (_, i) => {
          const realVal = i * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1);
          return <span key={i} className="w-8 text-center -ml-4" data-id="element-13659" data-mp="1ytebf3jzvzfg">
                  {formatNL(realVal)}
                </span>;
        })}
        </div>
        <div className="text-center mt-1 text-[9px] text-slate-400" data-id="element-13660" data-mp="1uunmhjpono3f">
          cm op kaart / {unit} in het echt
        </div>
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border-2 border-emerald-200 shadow-sm" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-13661" data-mp="3p73na4zpma0w">
      
      {label && <div className="mb-5 text-center" data-id="element-13662" data-mp="52z3kfygxoqh">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-emerald-200 shadow-sm" data-id="element-13663" data-mp="3n93zpeluixu9">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-13664" data-mp="3r9xnm7zagcn5" />
            <span className="text-sm font-bold text-emerald-900" data-id="element-13665" data-mp="3djkcxxv0iju4">{label}</span>
          </div>
        </div>}

      {/* Scale ratio display */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4 text-center" data-id="element-13666" data-mp="3iblo1ggg24ik">
        <p className="text-xs text-emerald-600 font-medium mb-1" data-id="element-13667" data-mp="33mfotshlweao">Schaal</p>
        <motion.p initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="text-2xl font-bold font-mono text-emerald-900" data-id="element-13668" data-mp="38jfz6flho37l">
          
          {formatScale(scaleRatio)}
        </motion.p>
        <p className="text-xs text-slate-500 mt-1" data-id="element-13669" data-mp="2zlcd9i6ld4f9">
          1 cm op de kaart = {formatNL(scaleRatio)} cm in het echt
        </p>
      </div>

      {/* Scale bar visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-13670" data-mp="2b0hbuyqj9l40">
        {/* Top labels (Map length) */}
        <div className="flex justify-between text-[10px] text-slate-500 mb-1" style={{
        maxWidth: `${segments * 56}px`,
        margin: '0 auto'
      }} data-id="element-13671" data-mp="2ow32facf0cyj">
          
          {Array.from({
          length: segments + 1
        }, (_, i) => <span key={i} className="w-4 text-center -ml-2" data-id="element-13672" data-mp="2twh8wnydbeug">
                {i}
              </span>)}
        </div>
        <div className="flex items-end justify-center gap-0 mb-1" data-id="element-13673" data-mp="38l3p0rvtkamq">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.2 + i * 0.1,
          duration: 0.3
        }} className={`h-4 w-14 border border-emerald-500 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-13674" data-mp="33omxrotbpg5f" data-mpi={i} />)}
        </div>
        {/* Bottom labels (Real length) */}
        <div className="flex justify-between text-[10px] text-emerald-700 font-bold" style={{
        maxWidth: `${segments * 56}px`,
        margin: '0 auto'
      }} data-id="element-13675" data-mp="2faqdfqxvn573">
          
          {Array.from({
          length: segments + 1
        }, (_, i) => {
          const realVal = i * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1);
          return <span key={i} className="w-8 text-center -ml-4" data-id="element-13676" data-mp="2y06kyy7zbxx0">
                  {formatNL(realVal)}
                </span>;
        })}
        </div>
        <div className="text-center mt-2 text-xs text-slate-500" data-id="element-13677" data-mp="330krgbtxmzsx">
          Boven: cm op kaart | Onder: {UNIT_LABELS[unit]} in het echt
        </div>
      </div>

      {/* Calculation example */}
      {(calculatedReal !== undefined || calculatedMap !== undefined) && <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-13678" data-mp="1cmkbbnsila5p">
          <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2" data-id="element-13679" data-mp="153az7msmixcy">
            Berekening
          </h4>
          {calculatedMap !== undefined && calculatedReal !== undefined && <div className="space-y-1 text-sm text-slate-700" data-id="element-13680" data-mp="10arnkvdhycas">
              <p data-id="element-13681" data-mp="ve14dz6eb0l5">
                <strong data-id="element-13682" data-mp="vbf0qamml587">{formatNL(calculatedMap)} cm</strong> op de kaart
              </p>
              <p className="text-emerald-600" data-id="element-13683" data-mp="rdqz6oqfge7c">× {formatNL(scaleRatio)}</p>
              <div className="border-t border-emerald-200 pt-1" data-id="element-13684" data-mp="1o0vo2tve1vxk">
                <p data-id="element-13685" data-mp="1j3c0h3csw6sy">
                  ={' '}
                  <strong data-id="element-13686" data-mp="1j3loileac3w0">
                    {formatNL(calculatedReal)} {unit}
                  </strong>{' '}
                  in het echt
                </p>
              </div>
            </div>}
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 shadow-sm" data-id="element-13687" data-mp="3m2450m81bmp">
        <div className="flex items-start gap-3" data-id="element-13688" data-mp="3usmo60duzja8">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13689" data-mp="3urtr5r4pc85a">
            <span className="text-emerald-600 text-sm" data-id="element-13690" data-mp="1ynx6367k247m">🗺️</span>
          </div>
          <div data-id="element-13691" data-mp="22s4sgwqjj82o">
            <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-13692" data-mp="22smygl3090ns">
              Wat betekent de schaal?
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13693" data-mp="1otnbriz1yvu9">
              Schaal <strong data-id="element-13694" data-mp="1tqx7fgd1c3qj">{formatScale(scaleRatio)}</strong> betekent: elke{' '}
              <strong data-id="element-13695" data-mp="2hh104sooqj2r">1 cm</strong> op de kaart is in werkelijkheid{' '}
              <strong data-id="element-13696" data-mp="2met2yhqvbplq">{formatNL(scaleRatio)} cm</strong>
              {scaleRatio >= 100 && <span data-id="element-13697" data-mp="2mgvq8u0mz4mr">
                  {' '}
                  ({formatNL(scaleRatio / 100)} {UNIT_LABELS.m})
                </span>}
              {scaleRatio >= 100000 && <span data-id="element-13698" data-mp="2cnyfja1vyjyp">
                  {' '}
                  = {formatNL(scaleRatio / 100000)} {UNIT_LABELS.km}
                </span>}
              .
            </p>
          </div>
        </div>
      </div>
    </div>;
}